import React, { useState, useEffect, useId, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../trial/UpgradeModal.scss';
import './Login.scss';

const TEACHER_PRICE_MONTHLY = 15;
const TEACHER_PRICE_ANNUAL = 9;
const SCHOOL_PRICE_PER_SEAT_MONTHLY = 15;
const SCHOOL_PRICE_PER_SEAT_ANNUAL = 9;

export const PENDING_PLAN_KEY = 'pending_checkout_plan';

export interface PendingPlan {
  plan_type: 'monthly' | 'yearly';
  seat_count: number;
}

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [schoolSeatsInput, setSchoolSeatsInput] = useState('1');
  const schoolSeats = Math.min(10, Math.max(1, parseInt(schoolSeatsInput, 10) || 1));
  const seatInputId = useId();
  const seatPreviewId = useId();
  const [redirecting, setRedirecting] = useState<string | null>(null);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (redirectTimer.current !== null) clearTimeout(redirectTimer.current);
    };
  }, []);

  const teacherPrice = isAnnual ? TEACHER_PRICE_ANNUAL : TEACHER_PRICE_MONTHLY;
  const schoolPricePerSeat = isAnnual ? SCHOOL_PRICE_PER_SEAT_ANNUAL : SCHOOL_PRICE_PER_SEAT_MONTHLY;
  const schoolTotalSeats = schoolSeats + 1;
  const schoolTotal = schoolTotalSeats * schoolPricePerSeat;
  const teacherAnnualTotal = teacherPrice * 12;
  const schoolAnnualTotal = schoolTotal * 12;

  const selectPlan = (plan_type: 'monthly' | 'yearly', seat_count: number, planLabel: string) => {
    const pending: PendingPlan = { plan_type, seat_count };
    localStorage.setItem(PENDING_PLAN_KEY, JSON.stringify(pending));
    setRedirecting(planLabel);
    if (redirectTimer.current !== null) clearTimeout(redirectTimer.current);
    redirectTimer.current = setTimeout(() => navigate('/signup'), 5000);
  };

  const startTrial = () => {
    setRedirecting('trial');
    if (redirectTimer.current !== null) clearTimeout(redirectTimer.current);
    redirectTimer.current = setTimeout(() => navigate('/signup'), 5000);
  };

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    return () => {
      html.style.overflow = '';
      html.style.height = '';
      body.style.overflow = '';
      body.style.height = '';
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="pricing-page__navbar-band">
        <div className="pricing-navbar">
          <img src="/assets/branding/rc-logo-dark.png" alt="Reality Coach" className="pricing-navbar__logo" />
          <div className="pricing-navbar__actions">
            <Link to="/login" className="pricing-navbar__signin">Sign In</Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="pricing-page__intro-band">
        <div className="upgrade-modal__header">
          <div className="upgrade-modal__top-badge">Reality Coach Pricing</div>
          <h1 className="upgrade-modal__title">Choose the option that best fits your school</h1>
          <p className="upgrade-modal__subtitle">
            Compare an individual teacher plan, a school purchase, and a district option. Toggle updates
            pricing. Only School uses a seat selector.
          </p>

          {/* Billing toggle */}
          <div className="upgrade-modal__toggle">
            <span className={!isAnnual ? 'upgrade-modal__toggle-label--active' : 'upgrade-modal__toggle-label'}>
              Monthly view
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isAnnual}
              className={`upgrade-modal__toggle-btn${isAnnual ? ' upgrade-modal__toggle-btn--on' : ''}`}
              onClick={() => setIsAnnual(v => !v)}
              aria-label="Annual billing"
            >
              <span className="upgrade-modal__toggle-knob" />
            </button>
            <span className={isAnnual ? 'upgrade-modal__toggle-label--active' : 'upgrade-modal__toggle-label'}>
              Annual view
            </span>
            {isAnnual && <span className="upgrade-modal__discount-badge">40% Discount</span>}
          </div>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="pricing-page__cards-band">
        <div className="pricing-page__cards-inner">
        <div className="upgrade-modal__cards">

          {/* Trial */}
          <div className="upgrade-modal__card">
            <h2 className="upgrade-modal__card-name">Trial</h2>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount">14 Day Trial</span>
            </div>
            <div className="upgrade-modal__card-billing">14 days or 5 sessions</div>
            <button
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              onClick={startTrial}
              disabled={redirecting !== null}
            >
              {redirecting === 'trial' ? 'Redirecting to signup…' : 'Start free trial'}
            </button>
            <p className="upgrade-modal__card-tagline">
              Try Reality Coach free for 14 days or 5 roleplay sessions, whichever comes first. No credit card
              required.
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>Full platform access</li>
              <li>Post-session feedback</li>
              <li>No credit card required</li>
              <li>Upgrade anytime</li>
            </ul>
          </div>

          {/* Teacher */}
          <div className="upgrade-modal__card">
            <h2 className="upgrade-modal__card-name">Teacher</h2>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount">${isAnnual ? teacherAnnualTotal : teacherPrice}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              per teacher / {isAnnual ? 'year, billed annually' : 'month, billed monthly'}
            </div>
            {isAnnual && (
              <p className="upgrade-modal__price-subtext">
                ${teacherPrice} / month | ${teacherAnnualTotal} / year
              </p>
            )}
            <button
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              onClick={() => selectPlan(isAnnual ? 'yearly' : 'monthly', 1, 'teacher')}
              disabled={redirecting !== null}
            >
              {redirecting === 'teacher' ? 'Redirecting to signup…' : 'Choose teacher'}
            </button>
            <p className="upgrade-modal__card-tagline">
              Individual access for one teacher or staff member practicing real student conversations.
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>Unlimited roleplays</li>
              <li>Post-session feedback</li>
              <li>Personal analytics dashboard</li>
              <li>Badges, streaks, and progress milestones</li>
            </ul>
          </div>

          {/* School — Recommended */}
          <div className="upgrade-modal__card upgrade-modal__card--recommended">
            <div className="upgrade-modal__recommended-tag">RECOMMENDED</div>
            <h2 className="upgrade-modal__card-name">School</h2>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount">${isAnnual ? schoolAnnualTotal : schoolTotal}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              {schoolTotalSeats} seats (1 buyer + {schoolSeats} teacher{schoolSeats !== 1 ? 's' : ''}) / {isAnnual ? 'year, billed annually' : 'month, billed monthly'}
            </div>
            {isAnnual && (
              <p className="upgrade-modal__price-subtext">
                ${schoolTotal} / month | ${schoolAnnualTotal} / year
              </p>
            )}
            <button
              className="upgrade-modal__card-cta upgrade-modal__card-cta--filled"
              onClick={() => selectPlan(isAnnual ? 'yearly' : 'monthly', schoolTotalSeats, 'school')}
              disabled={redirecting !== null}
            >
              {redirecting === 'school' ? 'Redirecting to signup…' : 'Choose school'}
            </button>
            <p className="upgrade-modal__card-tagline">
              School purchase for 1 to 10 teachers with shared oversight for implementation and review.
            </p>
            <div className="upgrade-modal__seat-selector">
              <label className="upgrade-modal__seat-label" htmlFor={seatInputId}>Number of teachers</label>
              <input
                id={seatInputId}
                aria-describedby={seatPreviewId}
                type="number"
                className="upgrade-modal__seat-select"
                min={1}
                max={10}
                value={schoolSeatsInput}
                onChange={e => { const v = e.target.value; if (v === '' || /^\d+$/.test(v)) setSchoolSeatsInput(v); }}
                onBlur={() => setSchoolSeatsInput(String(schoolSeats))}
                disabled={redirecting !== null}
              />
              <p className="upgrade-modal__seat-preview" id={seatPreviewId}>
                {schoolTotalSeats} seats total (1 buyer + {schoolSeats} teacher{schoolSeats !== 1 ? 's' : ''}) × ${schoolPricePerSeat}
              </p>
            </div>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>Everything in Teacher</li>
              <li>School dashboards and reporting</li>
              <li>User management with role-based access</li>
              <li>Organization-level analytics and oversight</li>
            </ul>
          </div>

          {/* District */}
          <div className="upgrade-modal__card">
            <h2 className="upgrade-modal__card-name">District</h2>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount upgrade-modal__price-amount--talk">Let's talk</span>
            </div>
            <div className="upgrade-modal__card-billing">
              custom quote<br />district purchasing and rollout
            </div>
            <a
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              href="mailto:sales@realitycoach.ai"
            >
              Contact sales
            </a>
            <p className="upgrade-modal__card-tagline">
              Districtwide purchasing with the same platform capabilities as Teacher and School.
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>Everything in Teacher and School</li>
              <li>District dashboards and reporting</li>
              <li>Administrator tools and user management</li>
              <li>Custom quote through sales</li>
            </ul>
          </div>

        </div>

        {/* Google Cloud Marketplace (secondary option) */}
        <div className="pricing-gcp-strip">
          <div className="pricing-gcp-strip__info">
            <div className="pricing-gcp-strip__title">Already using Google Cloud?</div>
            <div className="pricing-gcp-strip__desc">
              Subscribe via your existing GCP billing account. Full platform access, no separate payment method
              needed.
            </div>
          </div>
          <a
            className="pricing-gcp-strip__cta"
            href="https://console.cloud.google.com/marketplace/product/da-realitycoach-prod/reality-coach"
            target="_blank"
            rel="noopener noreferrer"
          >
            Continue with Google Marketplace →
          </a>
        </div>
        </div>
      </div>

      {/* Footer */}
    </div>
  );
};

export default PricingPage;
